import React from 'react';
import { computeTargetGap } from '../../../logic/oap';
import EmptyState from '../shared/EmptyState';
import ComingSoonBanner from '../shared/ComingSoonBanner';

const fieldLabel = {
  display: 'block', marginBottom: 4, fontSize: 10, fontWeight: 600,
  textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--wi-text-secondary)',
};

// Medication review — clinician only. Shows the patient's REAL current regimen
// (care_plan.medications, edited for real in the Care Plan tab via WF15) plus
// real BP context. No fabricated history: medication changes are managed on the
// care plan, so the tab links there rather than offering a local prescribe flow.
export default function MedicationTab({ patient, userRole, carePlan, onOpenCarePlan }) {
  if (userRole !== 'clinician') {
    return <EmptyState compact title="Medication review is available to clinicians." />;
  }

  const meds = (Array.isArray(carePlan?.medications) ? carePlan.medications : []).filter(m => m && m.name);
  const hasBp = patient.lastSystolic != null;
  const targetGap = computeTargetGap(patient.firstSystolic, patient.lastSystolic);
  const onTarget = targetGap != null && targetGap <= 0;

  return (
    <div>
      <ComingSoonBanner />
      {/* ── BP context (real fields only) ── */}
      {(hasBp || targetGap != null) && (
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 20, padding: '14px 16px', background: 'var(--wi-background-secondary)', borderRadius: 8, border: '1px solid var(--wi-divider)' }}>
          {hasBp && (
            <div>
              <span style={fieldLabel}>Current BP</span>
              <span className="body2" style={{ fontWeight: 600 }}>{patient.lastSystolic}/{patient.lastDiastolic ?? '—'} mmHg</span>
            </div>
          )}
          {patient.firstSystolic != null && (
            <div>
              <span style={fieldLabel}>Baseline systolic</span>
              <span className="body2" style={{ fontWeight: 600 }}>{patient.firstSystolic} mmHg</span>
            </div>
          )}
          {targetGap != null && (
            <div>
              <span style={fieldLabel}>vs. target</span>
              <span className="body2" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 600, color: onTarget ? 'var(--wi-status-good)' : 'var(--wi-text-primary-moderate)' }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'currentColor' }} />
                {onTarget ? 'On target' : `+${targetGap} mmHg above target`}
              </span>
            </div>
          )}
        </div>
      )}

      {/* ── Section header ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '4px 0 10px', gap: 12, flexWrap: 'wrap' }}>
        <span className="eyebrow text-secondary">Current medications</span>
        {meds.length > 0 && onOpenCarePlan && (
          <button className="btn-secondary" style={{ fontSize: 12, padding: '6px 14px' }} onClick={onOpenCarePlan}>
            Edit in Care Plan
          </button>
        )}
      </div>

      {/* ── Real regimen, or honest empty state ── */}
      {meds.length === 0 ? (
        <EmptyState
          title="No medications on the care plan yet"
          description="Medications are managed on the care plan. Add the patient's regimen there to see it here."
          action={onOpenCarePlan && (
            <button className="btn-secondary" style={{ fontSize: 12, padding: '6px 14px' }} onClick={onOpenCarePlan}>
              Open Care Plan
            </button>
          )}
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {/* Same row + chip vocabulary as CarePlanTab's Medications section. */}
          {meds.map((m, i) => (
            <div key={i} style={{ background: 'var(--wi-background-secondary)', borderRadius: 8, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--wi-text-primary)' }}>
                {m.name}{m.dose ? ` · ${m.dose}` : ''}
              </span>
              {m.frequency && (
                <span style={{ fontSize: 12, color: 'var(--wi-text-secondary)' }}>{m.frequency}</span>
              )}
              {m.class && (
                <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', height: 20, padding: '0 8px', borderRadius: 6, fontSize: 10, fontWeight: 500, background: 'var(--wi-background-tertiary)', color: 'var(--wi-text-secondary)' }}>
                  {m.class}
                </span>
              )}
            </div>
          ))}
          <p className="detail1 text-secondary" style={{ margin: '8px 0 0' }}>
            From the care plan. Dose changes and additions are made in the Care Plan tab.
          </p>
        </div>
      )}
    </div>
  );
}
